import MaplibreGeocoder from '@maplibre/maplibre-gl-geocoder'
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'

import { useRef, useLayoutEffect, useEffect, useMemo } from 'react'
import { DEFAULT_MAP_OPTIONS } from '../common/constants'

export enum MapInstanceID {
  MODAL = 'modal',
  EDITOR = 'editor',
}

let mapInstances: { [id: string]: { container: HTMLElement; map: maplibregl.Map; geocoder: MaplibreGeocoder } } = {}

const mapRoot = document.getElementById('map-root')!

const protocol = new Protocol()
maplibregl.addProtocol('pmtiles', protocol.tile.bind(protocol))

const getMapInstance = (id: MapInstanceID) => {
  if (mapInstances[id] == null) {
    const mapContainer = document.createElement('div')
    mapContainer.style.width = '100%'
    mapContainer.style.height = '100%'
    mapContainer.style.background = '#1a3a5c'
    mapContainer.style.position = 'relative'

    // Star field canvas behind the map
    const starCanvas = document.createElement('canvas')
    starCanvas.style.position = 'absolute'
    starCanvas.style.inset = '0'
    starCanvas.style.width = '100%'
    starCanvas.style.height = '100%'
    starCanvas.style.pointerEvents = 'none'
    mapContainer.appendChild(starCanvas)

    const stars: { x: number; y: number; size: number; brightness: number }[] = []
    for (let i = 0; i < 400; i++) {
      stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.5 + 0.5,
        brightness: Math.random() * 0.6 + 0.2,
      })
    }

    const drawSpaceEffects = (mapInstance: maplibregl.Map) => {
      const rect = starCanvas.getBoundingClientRect()
      const dpr = window.devicePixelRatio || 1
      starCanvas.width = rect.width * dpr
      starCanvas.height = rect.height * dpr
      const ctx = starCanvas.getContext('2d')
      if (!ctx) return
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, rect.width, rect.height)

      const center = mapInstance.getCenter()
      const zoom = mapInstance.getZoom()

      // Draw stars
      for (const star of stars) {
        const sx = ((star.x + center.lng / 360) % 1 + 1) % 1 * rect.width
        const sy = ((star.y + center.lat / 180) % 1 + 1) % 1 * rect.height
        ctx.beginPath()
        ctx.arc(sx, sy, star.size, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`
        ctx.fill()
      }

      // Draw atmospheric glow around globe edge (only visible when zoomed out)
      if (zoom < 5) {
        try {
          const screenCenter = mapInstance.project(center)
          // Project a point ~89° away to find the globe edge radius
          const edgePoint = mapInstance.project(
            new maplibregl.LngLat(
              (center.lng + 89) % 360,
              center.lat,
            ),
          )
          const dx = edgePoint.x - screenCenter.x
          const dy = edgePoint.y - screenCenter.y
          const globeRadius = Math.sqrt(dx * dx + dy * dy)

          if (globeRadius > 10 && globeRadius < rect.width) {
            const glowWidth = globeRadius * 0.35

            // Outer soft glow — wide and prominent
            const outerGradient = ctx.createRadialGradient(
              screenCenter.x, screenCenter.y, globeRadius,
              screenCenter.x, screenCenter.y, globeRadius + glowWidth,
            )
            outerGradient.addColorStop(0, 'rgba(200, 225, 255, 0.9)')
            outerGradient.addColorStop(0.2, 'rgba(170, 205, 255, 0.5)')
            outerGradient.addColorStop(0.5, 'rgba(140, 185, 255, 0.2)')
            outerGradient.addColorStop(1, 'rgba(100, 150, 255, 0)')

            ctx.beginPath()
            ctx.arc(screenCenter.x, screenCenter.y, globeRadius + glowWidth, 0, Math.PI * 2)
            ctx.arc(screenCenter.x, screenCenter.y, globeRadius, 0, Math.PI * 2, true)
            ctx.fillStyle = outerGradient
            ctx.fill()

            // Inner bright edge — white rim at the globe border
            const innerGradient = ctx.createRadialGradient(
              screenCenter.x, screenCenter.y, globeRadius - 6,
              screenCenter.x, screenCenter.y, globeRadius + glowWidth * 0.2,
            )
            innerGradient.addColorStop(0, 'rgba(255, 255, 255, 0)')
            innerGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.9)')
            innerGradient.addColorStop(0.6, 'rgba(220, 235, 255, 0.5)')
            innerGradient.addColorStop(1, 'rgba(180, 210, 255, 0)')

            ctx.beginPath()
            ctx.arc(screenCenter.x, screenCenter.y, globeRadius + glowWidth * 0.2, 0, Math.PI * 2)
            ctx.arc(screenCenter.x, screenCenter.y, globeRadius - 6, 0, Math.PI * 2, true)
            ctx.fillStyle = innerGradient
            ctx.fill()
          }
        } catch {
          // projection may fail at extreme values
        }
      }
    }
    mapRoot.appendChild(mapContainer)
    //style: 'mapbox://styles/mapbox/streets-v12',
    const map = new maplibregl.Map({
      container: mapContainer,
      style: 'https://tiles.seppo.io/style7.json',
      trackResize: true,
      zoom: DEFAULT_MAP_OPTIONS.zoom,
      center: [...DEFAULT_MAP_OPTIONS.center],
      canvasContextAttributes: {
        preserveDrawingBuffer: true,
      },
    })
    map.on('style.load', () => {
      map.setProjection({ type: 'globe' })
      map.setSky({
        'sky-color': '#76b1e8',
        'sky-horizon-blend': 0.3,
        'horizon-color': '#b8d4ee',
        'horizon-fog-blend': 0.5,
        'fog-color': '#ccdcee',
        'fog-ground-blend': 0.05,
        'atmosphere-blend': [
          'interpolate',
          ['linear'],
          ['zoom'],
          0, 0.6,
          3, 0.4,
          5, 0.1,
          7, 0,
        ],
      })
    })

    // Draw space effects (stars + glow) and update on map move
    map.on('load', () => drawSpaceEffects(map))
    map.on('move', () => drawSpaceEffects(map))
    map.on('resize', () => drawSpaceEffects(map))
    map.on('click', (e) => {
      console.log(
        map.queryRenderedFeatures(e.point).map((f) => ({
          layer: f.layer.id,
          sourceLayer: f.sourceLayer,
          props: f.properties,
        })),
      )
    })
    const geocoder = new MaplibreGeocoder(
      {
        forwardGeocode: async (config) => {
          const searchText =
            typeof config.query === 'string' ? config.query : typeof config.query === 'object' ? config.query[0] : ''
          const url = `https://nominatim.openstreetmap.org/search?format=geojson&q=${encodeURIComponent(searchText)}`
          const response = await fetch(url)
          const geojson = await response.json()

          const features = (geojson.features ?? []).map((f: any) => {
            const [lng, lat] = f.geometry?.coordinates ?? []
            const display = f.properties?.display_name ?? f.properties?.name ?? searchText

            return {
              type: 'Feature',
              geometry: f.geometry,
              bbox: f.bbox,
              center: [lng, lat], // 👈 Carmen-style convenience field
              place_name: display, // 👈 what the UI shows
              text: f.properties?.name ?? display,
              properties: {
                ...f.properties,
              },
            }
          })

          return {
            type: 'FeatureCollection',
            features,
          }
        },
      },
      {
        maplibregl: maplibregl,
        showResultsWhileTyping: true,
        minLength: 3,
      },
    )
    map.addControl(geocoder, 'top-right')
    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    if (id === MapInstanceID.EDITOR) {
      map.on('load', () => {
        map.addSource('line', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        })
        map.addLayer({
          id: 'line-layer',
          type: 'line',
          source: 'line',
          layout: {
            'line-cap': 'round',
          },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 5,
            'line-opacity': 0.8,
            'line-dasharray': [1, 2],
          },
        })
      })
    }

    mapInstances[id] = { container: mapContainer, map, geocoder }
  }
  return mapInstances[id]
}

export const tryMoveMapTo = (id: MapInstanceID, element: HTMLElement) => {
  if (mapInstances[id] != null) {
    element.appendChild(mapInstances[id].container)
    getMapInstance(id).geocoder.clear()
  }
}

// IMPORTANT: when unmounting any component that has the map in it, we MUST first move
// the map back to it's original position - otherwise we lose the map
export const tryResetMapInstance = (id: MapInstanceID) => {
  tryMoveMapTo(id, mapRoot)
}

// NOTE: Only use inside MapboxContainer component
export const useMap = (id: MapInstanceID) => {
  const mapContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Ensuring map exists
    getMapInstance(id)
  }, [id])

  useLayoutEffect(() => {
    if (mapContainerRef.current != null) {
      tryMoveMapTo(id, mapContainerRef.current)
      getMapInstance(id).map.resize()
    }
    return () => tryResetMapInstance(id)
  }, [id])

  // Cache return object to avoid unnecessary re-renders
  const ret = useMemo(
    () => ({
      elementRef: mapContainerRef,
      api: getMapInstance(id).map,
      geocoder: getMapInstance(id).geocoder,
    }),
    [id],
  )

  return ret
}

// NOTE: for use outside of MapboxContainer component
export const useMapApi = getMapInstance
